import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
    return twMerge(clsx(inputs))
}

const Popover = ({ children, open: controlledOpen, onOpenChange: setControlledOpen }) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const triggerRef = useRef(null);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const onOpenChange = isControlled ? setControlledOpen : setUncontrolledOpen;

    return (
        <div className="relative inline-block text-left" ref={triggerRef}>
            {React.Children.map(children, child =>
                React.isValidElement(child) ? React.cloneElement(child, { open, onOpenChange, triggerRef }) : child
            )}
        </div>
    )
}

const PopoverTrigger = React.forwardRef(({ className, children, asChild, open, onOpenChange, triggerRef, ...props }, ref) => {
    const Comp = asChild ? React.Children.only(children) : 'button';
    return React.cloneElement(Comp, {
        ref,
        onClick: (e) => {
            e.stopPropagation();
            if (Comp.props.onClick) Comp.props.onClick(e);
            if (onOpenChange) onOpenChange(!open);
        },
        ...props
    });
})
PopoverTrigger.displayName = "PopoverTrigger"

const PopoverContent = React.forwardRef(({ className, align = "center", sideOffset = 4, open, onOpenChange, triggerRef, ...props }, ref) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const contentRef = useRef(null);

    useEffect(() => {
        if (open && triggerRef?.current) {
            // Function to update position
            const updatePosition = () => {
                const triggerRect = triggerRef.current.getBoundingClientRect();
                const scrollY = window.scrollY;
                const scrollX = window.scrollX;

                // Position below the trigger
                let top = triggerRect.bottom + scrollY + sideOffset;
                let left = triggerRect.left + scrollX;

                // Adjust if popover would go off-screen to the right
                const popoverWidth = contentRef.current ? contentRef.current.offsetWidth : 288;
                if (left + popoverWidth > window.innerWidth) {
                    left = window.innerWidth - popoverWidth - 16;
                }

                // Adjust if popover would go off-screen at the bottom
                if (contentRef.current) {
                    const contentRect = contentRef.current.getBoundingClientRect();
                    if (triggerRect.bottom + contentRect.height > window.innerHeight) {
                        // Position above the trigger instead
                        top = triggerRect.top + scrollY - contentRect.height - sideOffset;
                    }
                }

                setPosition({ top, left });
            };

            updatePosition();

            // Re-calculate on resize
            window.addEventListener('resize', updatePosition);
            return () => window.removeEventListener('resize', updatePosition);
        }
    }, [open, triggerRef, sideOffset]);

    if (!open) return null;

    // Use portal to render outside parent container (avoids overflow:hidden issues)
    return ReactDOM.createPortal(
        <>
            {/* Invisible backdrop to catch clicks outside the popover */}
            <div
                className="fixed inset-0 z-[9998]"
                onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenChange) onOpenChange(false);
                }}
            />
            {/* Popover content */}
            <div
                ref={(node) => {
                    contentRef.current = node;
                    if (typeof ref === 'function') ref(node);
                    else if (ref) ref.current = node;
                }}
                className={cn(
                    "fixed z-[9999] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-lg outline-none bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100",
                    className
                )}
                style={{ top: position.top, left: position.left }}
                onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing
                {...props}
            />
        </>,
        document.body
    )
})
PopoverContent.displayName = "PopoverContent"

export { Popover, PopoverTrigger, PopoverContent }
